import {
  Box,
  Stack,
  Typography,
  Chip,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Skeleton,
  TextField,
  Divider,
  LinearProgress
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { SearchNormal1 } from 'iconsax-reactjs';
import { useEffect, useState, useCallback, useRef } from 'react';
import { ArrowDown2 } from 'iconsax-reactjs';
import BackToggle from 'components/third-party/dropzone/BackToggle';
import { getAssessorWiseDetails } from 'services/assessorDetails.service';
import { getOverallResult, getPerformanceEvaluation, getBatchStudents } from 'services/evalution.service';
import IconButton from 'components/@extended/IconButton';
import { FormattedMessage } from 'react-intl';
import MainCard from 'components/MainCard';
import useAuth from 'hooks/useAuth';

interface BatchInfo {
  _id: string;
  name: string;
}
interface QualificationPack {
  _id: string;
  qpCode: string;
  qpName: string;
}
interface AssessorTemplate {
  _id: string;
  templateName: string;
  startDate: string;
  endDate: string;
  batch: BatchInfo;
  qualificationPack: QualificationPack;
  type: string;
  attemptNumber?: number;
  assignedAssessorId?: string;
}
interface OverallStudentRow {
  _id: string;
  studentId: string;
  studentName: string;
  email?: string;
  totalMarks: number;
  obtainedMarks: number;
  percentage: number;
  result: string;
  evaluatedByAssessorId?: string;
}

function buildStudentWithMarks(batchData: any, evalData: any, studentId: string) {
  const marksByPc: Record<string, any> = {};
  const rawEval: any[] = evalData?.evaluation || [];
  const isReassessment = String(batchData?.sessionType ?? '').toLowerCase() === 'reassessment';
  // Reassessment: only NOS that were re-marked (failed scope). Never fall back to full QP from batch.
  const evaluation: any[] = isReassessment ? rawEval.filter((n: any) => n.isReassessed === true) : rawEval;

  const nosList: any[] =
    evaluation.length > 0
      ? evaluation.map((nosEntry: any) => {
          // nosId may be populated object after getEvaluation populate
          const nosRef = nosEntry.nosId as any;
          return {
            nosId: nosRef?._id ?? nosRef,
            nosCode: nosRef?.nosCode ?? '',
            nosName: nosRef?.nosName ?? '',
            pcs: (nosEntry.pcs || []).map((pcEntry: any) => {
              const pcRef = pcEntry.pcId as any;
              return {
                pcId: pcRef?._id ?? pcRef,
                pcCode: pcRef?.pcCode ?? '',
                pcName: pcRef?.pcName ?? '',
                totalMarks: pcRef?.totalMarks ?? pcEntry.pcTotalMarks ?? 0,
                isCritical: pcRef?.isCritical ?? false,
                type: pcRef?.type ?? '',
                questions: (pcEntry.questions || []).map((q: any) => {
                  const qRef = q.questionId as any;
                  return {
                    questionId: qRef?._id ?? qRef,
                    question: qRef?.question ?? '',
                    maxMarks: 0, // filled below from batchData
                    obtainedMarks: q.obtainedMarks ?? 0
                  };
                })
              };
            })
          };
        })
      : isReassessment
        ? []
        : batchData?.nos || [];

  // Build marksByPc from evaluation data
  evaluation.forEach((nosEntry: any) => {
    (nosEntry.pcs || []).forEach((pcEntry: any) => {
      const pcId = (pcEntry.pcId?._id || pcEntry.pcId)?.toString?.();
      if (!pcId) return;

      // Find PC details from batchData for maxMarks/metadata
      let pcMeta: any = null;
      for (const nos of batchData?.nos || []) {
        pcMeta = (nos.pcs || []).find((p: any) => (p.pcId?._id ?? p.pcId)?.toString?.() === pcId);
        if (pcMeta) break;
      }

      const rawQuestions = pcMeta?.questions || [];
      const questions = rawQuestions.map((q: any) => {
        const qId = (q.questionId?._id ?? q.questionId ?? q.qId)?.toString?.();
        const found = (pcEntry.questions || []).find((eq: any) => (eq.questionId?._id || eq.questionId)?.toString?.() === qId);
        return {
          qId: q.questionId ?? q.qId,
          questionText: q.question ?? q.questionText ?? '',
          maxMarks: q.maxMarks ?? q.marks ?? '',
          marks: found != null ? String(found.obtainedMarks ?? '') : ''
        };
      });

      marksByPc[pcId] = {
        pcId,
        pcCode: pcMeta?.pcCode ?? (pcEntry.pcId as any)?.pcCode ?? '',
        pcName: pcMeta?.pcName ?? (pcEntry.pcId as any)?.pcName ?? '',
        maxMarks: pcMeta?.totalMarks ?? pcEntry.pcTotalMarks ?? 0,
        isCritical: Boolean(pcMeta?.isCritical),
        type: pcMeta?.type,
        questions
      };
    });
  });

  return { id: studentId, marksByPc, nosList };
}

const stringToHue = (str: string) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return Math.abs(hash) % 360;
};

const ResultDetails = () => {
  const { user } = useAuth();
  const [templates, setTemplates] = useState<AssessorTemplate[]>([]);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [inputValue, setInputValue] = useState('');
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [sort] = useState<Array<{ id: string; desc?: boolean }>>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<AssessorTemplate | null>(null);
  const [students, setStudents] = useState<OverallStudentRow[]>([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [batchInfo, setBatchInfo] = useState<any>(null);
  const [batchInfoLoading, setBatchInfoLoading] = useState(false);
  const [evalByStudent, setEvalByStudent] = useState<Record<string, { loading: boolean; data: any | null }>>({});
  const [expandedStudent, setExpandedStudent] = useState<string | false>(false);

  const handleSearchInputChange = useCallback((value: string) => {
    setInputValue(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearch(value);
      setPageIndex(1);
    }, 500);
  }, []);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const res = await getAssessorWiseDetails({ page: pageIndex, limit: pageSize, search: search || undefined, result: true });
        const data = res?.data?.data?.data || [];
        setTemplates(data);
        if (!selectedTemplate && data.length > 0) setSelectedTemplate(data[0]);
      } catch (e) {
        console.error(e);
      }
    };
    fetchTemplates();
  }, [pageIndex, pageSize, search, sort]);

  useEffect(() => {
    const loadStudents = async () => {
      if (!selectedTemplate?.batch?._id) {
        setStudents([]);
        return;
      }
      setStudentsLoading(true);
      try {
        const res: any = await getOverallResult(selectedTemplate.batch._id, {
          page: pageIndex,
          limit: pageSize,
          sortColumn: sort?.[0]?.id,
          sortOrder: sort?.[0]?.desc ? 'desc' : 'asc',
          assessmentSessionId: selectedTemplate._id
        });
        const payload = res?.data?.data ?? res?.data;
        let data: any[] = payload?.data ?? (Array.isArray(payload) ? payload : []);
        if (selectedTemplate?.type === 'reassessment') data = data.filter((r: any) => r.result === 'Pass' || r.result === 'Fail');
        setStudents(
          data.map((row: any) => ({
            _id: row._id,
            studentId: row.studentId?._id || row.studentId || row._id,
            studentName: row.studentName || `${row.firstName || ''} ${row.lastName || ''}`.trim(),
            email: row.email,
            totalMarks: Number(row.totalMarks) || 0,
            obtainedMarks: Number(row.obtainedMarks) || 0,
            percentage: Number(row.percentage) || 0,
            result: row.result || '—',
            evaluatedByAssessorId: row.evaluatedByAssessorId != null ? String(row.evaluatedByAssessorId) : ''
          }))
        );
      } catch (e) {
        console.error(e);
        setStudents([]);
      } finally {
        setStudentsLoading(false);
      }
    };
    loadStudents();
  }, [selectedTemplate, pageIndex, pageSize, sort]);

  useEffect(() => {
    const loadBatchInfo = async () => {
      if (!selectedTemplate?.batch?._id || !selectedTemplate?._id) {
        setBatchInfo(null);
        return;
      }
      setBatchInfoLoading(true);
      try {
        const res: any = await getBatchStudents(selectedTemplate.batch._id, selectedTemplate._id);
        const data = res?.data?.data || res?.data || null;
        setBatchInfo(data);
      } catch (e) {
        console.error('Failed to load batch structure', e);
        setBatchInfo(null);
      } finally {
        setBatchInfoLoading(false);
      }
    };
    loadBatchInfo();
    // reset per-student eval cache when session changes
    setEvalByStudent({});
    setExpandedStudent(false);
  }, [selectedTemplate]);

  const loadEvalForStudent = useCallback(
    async (s: OverallStudentRow) => {
      if (!selectedTemplate?.batch?._id || !selectedTemplate?._id) return;
      if (evalByStudent[s.studentId]?.loading || evalByStudent[s.studentId]?.data) return;
      setEvalByStudent((prev) => ({ ...prev, [s.studentId]: { loading: true, data: null } }));
      try {
        const res = await getPerformanceEvaluation(selectedTemplate.batch._id, s.studentId, selectedTemplate._id);
        const data = res?.data?.data || res?.data || null;
        setEvalByStudent((prev) => ({ ...prev, [s.studentId]: { loading: false, data } }));
      } catch {
        setEvalByStudent((prev) => ({ ...prev, [s.studentId]: { loading: false, data: null } }));
      }
    },
    [selectedTemplate, evalByStudent]
  );

  return (
    <Box display="flex" flexDirection="column" gap={2} sx={{ pb: 4 }}>
      {/* Header */}
      <MainCard
        title={
          <Stack direction="row" alignItems="center" spacing={2}>
            <BackToggle show />
            <Box>
              <Typography variant="h5">
                <FormattedMessage id="Result Details" />
              </Typography>
              <Typography variant="caption" color="text.secondary">
                <FormattedMessage id="View student-wise result history for released assessments of batches." />
              </Typography>
            </Box>
          </Stack>
        }
        content={false}
        secondary={
          <Stack direction="row" spacing={1} alignItems="center" sx={{ pr: 2 }}>
            <TextField
              placeholder="Search batch or template..."
              size="small"
              value={inputValue}
              onChange={(e) => handleSearchInputChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') setPageIndex(1);
              }}
              // sx={{ width: 240 }}
            />
            <IconButton variant="outlined" onClick={() => setPageIndex(1)}>
              <SearchNormal1 />
            </IconButton>
          </Stack>
        }
      />

      {/* Batch Selector */}
      <Box sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', p: 2 }}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid size={{ xs: 12, md: 5 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
              textTransform="uppercase"
              letterSpacing={0.5}
              sx={{ mb: 0.5, display: 'block' }}
            >
              Batch / Session
            </Typography>
            <TextField
              select
              fullWidth
              size="small"
              value={selectedTemplate?._id || ''}
              onChange={(e) => {
                setSelectedTemplate(templates.find((t) => t._id === e.target.value) || null);
                setPageIndex(1);
              }}
              SelectProps={{ native: true }}
            >
              <option value="">— Select batch —</option>
              {templates.map((t) => (
                <option key={t._id} value={t._id}>
                  {t.batch?.name || '—'} · {t.templateName} (Attempt {t.attemptNumber || 1})
                </option>
              ))}
            </TextField>
          </Grid>
          {selectedTemplate && (
            <>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={600}
                  textTransform="uppercase"
                  letterSpacing={0.5}
                  sx={{ mb: 0.5, display: 'block' }}
                >
                  Qualification Pack
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip label={selectedTemplate.qualificationPack.qpCode} size="small" />
                  <Typography variant="body2">{selectedTemplate.qualificationPack.qpName}</Typography>
                </Stack>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={600}
                  textTransform="uppercase"
                  letterSpacing={0.5}
                  sx={{ mb: 0.5, display: 'block' }}
                >
                  Type & Attempt
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip label={selectedTemplate.type} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />
                  <Chip label={`Attempt ${selectedTemplate.attemptNumber || 1}`} size="small" variant="outlined" />
                </Stack>
              </Grid>
            </>
          )}
        </Grid>
      </Box>

      {/* Student List */}
      {selectedTemplate && (
        <Box sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', overflow: 'hidden' }}>
          <Box>
            {studentsLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <Box key={i} sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Skeleton variant="circular" width={34} height={34} />
                      <Box>
                        <Skeleton width={140} height={14} />
                        <Skeleton width={100} height={12} />
                      </Box>
                    </Stack>
                    <Skeleton width={52} height={24} sx={{ borderRadius: 1 }} />
                  </Stack>
                </Box>
              ))
            ) : students.length === 0 ? (
              <Box sx={{ py: 6, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No result data available for this batch.
                </Typography>
              </Box>
            ) : (
              students.map((s) => {
                const hue = stringToHue(s.studentName || 'S');
                const initials = s.studentName
                  ? s.studentName
                      .split(' ')
                      .map((n: string) => n[0])
                      .slice(0, 2)
                      .join('')
                      .toUpperCase()
                  : 'S';
                const isExpanded = expandedStudent === s.studentId;
                const evalState = evalByStudent[s.studentId];
                const isAssessorAdmin = user?.type === 'assessorAdmin';
                const finalResult = s.result === 'Pass' || s.result === 'Fail';
                const currentUserId = String(user?._id ?? user?.id ?? '');
                const sessionAssignedToMe =
                  Boolean(selectedTemplate?.assignedAssessorId) && String(selectedTemplate.assignedAssessorId) === currentUserId;
                const completedByOtherAssessor =
                  isAssessorAdmin && finalResult && !sessionAssignedToMe && String(s.evaluatedByAssessorId || '') !== currentUserId;

                if (completedByOtherAssessor) {
                  return (
                    <Box
                      key={s.studentId}
                      sx={{
                        px: 2.5,
                        py: 1.5,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'background.paper',
                        '&:last-child': { borderBottom: 'none' }
                      }}
                    >
                      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
                          <Avatar
                            sx={{ width: 32, height: 32, fontSize: 11, fontWeight: 700, bgcolor: `hsl(${hue},45%,55%)`, flexShrink: 0 }}
                          >
                            {initials}
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={600} noWrap>
                              {s.studentName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {s.obtainedMarks} / {s.totalMarks} marks · {s.result}
                            </Typography>
                          </Box>
                        </Stack>
                        <Chip
                          label={s.result}
                          size="small"
                          variant="filled"
                          color={s.result === 'Pass' ? 'success' : 'error'}
                          sx={{ fontWeight: 700, fontSize: '0.7rem', flexShrink: 0 }}
                        />
                      </Stack>
                    </Box>
                  );
                }

                return (
                  <Accordion
                    key={s.studentId}
                    expanded={isExpanded}
                    onChange={(_, exp) => {
                      setExpandedStudent(exp ? s.studentId : false);
                      if (exp) loadEvalForStudent(s);
                    }}
                    disableGutters
                    elevation={0}
                    sx={{
                      '&:before': { display: 'none' },
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      '&:last-child': { borderBottom: 'none' }
                    }}
                  >
                    {/* Row: avatar + name + result chip + arrow */}
                    <AccordionSummary
                      sx={{
                        px: 2.5,
                        minHeight: 54,
                        '& .MuiAccordionSummary-content': { my: 0, alignItems: 'center', justifyContent: 'space-between' },
                        '& .MuiAccordionSummary-expandIconWrapper': {
                          transition: 'transform 0.2s',
                          '&.Mui-expanded': { transform: 'rotate(180deg)' }
                        }
                      }}
                      expandIcon={<ArrowDown2 fontSize="small" />}
                    >
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Avatar
                          sx={{ width: 32, height: 32, fontSize: 11, fontWeight: 700, bgcolor: `hsl(${hue},45%,55%)`, flexShrink: 0 }}
                        >
                          {initials}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {s.studentName}
                          </Typography>
                          {s.email && (
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {s.email}
                            </Typography>
                          )}
                        </Box>
                      </Stack>

                      <Chip
                        label={s.result}
                        size="small"
                        variant={s.result === 'Pass' || s.result === 'Fail' ? 'filled' : 'outlined'}
                        color={s.result === 'Pass' ? 'success' : s.result === 'Fail' ? 'error' : 'default'}
                        sx={{ fontWeight: 700, fontSize: '0.7rem', flexShrink: 0, mr: 1 }}
                      />
                    </AccordionSummary>

                    {/* Expanded: question-wise marks table */}
                    <AccordionDetails sx={{ px: 2.5, pt: 0, pb: 2.5, bgcolor: 'grey.50' }}>
                      <Divider sx={{ mb: 2 }} />

                      {/* Loading state */}
                      {(batchInfoLoading || !batchInfo || evalState?.loading) && (
                        <Stack spacing={0.75}>
                          {[80, 60, 70].map((w, i) => (
                            <Skeleton key={i} width={`${w}%`} height={13} />
                          ))}
                        </Stack>
                      )}

                      {batchInfo &&
                        evalState &&
                        !evalState.loading &&
                        evalState.data &&
                        (() => {
                          const built = buildStudentWithMarks(batchInfo, evalState.data, s.studentId);
                          const nosList: any[] = built.nosList;
                          const marksByPc = built.marksByPc || {};

                          const getPcTotal = (pcId: string) => {
                            const pc = marksByPc[pcId];
                            if (!pc) return 0;
                            return (pc.questions || []).reduce((sum: number, q: any) => sum + (Number(q.marks) || 0), 0);
                          };

                          const grandTotal = nosList.reduce(
                            (sum: number, nos: any) =>
                              sum +
                              (nos.pcs || []).reduce((s2: number, pc: any) => {
                                const pcId = String(pc?.pcId?._id || pc?.pcId);
                                return s2 + getPcTotal(pcId);
                              }, 0),
                            0
                          );
                          const grandMax = nosList.reduce(
                            (sum: number, nos: any) =>
                              sum + (nos.pcs || []).reduce((s2: number, pc: any) => s2 + (Number(pc.totalMarks) || 0), 0),
                            0
                          );

                          return (
                            <Box
                              sx={{ display: 'flex', flexDirection: 'column', gap: 0, borderRadius: 2, overflow: 'hidden', boxShadow: 1 }}
                            >
                              {nosList.map((nos: any, nosIdx: number) => {
                                const nosTotal = (nos.pcs || []).reduce((sum: number, pc: any) => {
                                  const pcId = pc?.pcId?._id || pc?.pcId;
                                  return sum + getPcTotal(String(pcId));
                                }, 0);
                                const nosMax = (nos.pcs || []).reduce((sum: number, pc: any) => {
                                  const pcId = String(pc?.pcId?._id || pc?.pcId);
                                  const pcData = marksByPc[pcId] || {};
                                  return sum + (Number(pc.totalMarks ?? pcData.maxMarks ?? 0) || 0);
                                }, 0);
                                const nosPercent = nosMax > 0 ? Math.round((nosTotal / nosMax) * 100) : 0;

                                return (
                                  <Accordion
                                    key={String(nos.nosId?._id || nos.nosId || nosIdx)}
                                    defaultExpanded={nosIdx === 0}
                                    disableGutters
                                    elevation={0}
                                    sx={{
                                      '&:before': { display: 'none' },
                                      border: '1px solid',
                                      borderColor: 'divider',
                                      borderRadius: '0 !important',
                                      '& + .MuiAccordion-root': { borderTop: 0 }
                                    }}
                                  >
                                    <AccordionSummary
                                      expandIcon={<ArrowDown2 size={18} />}
                                      sx={{
                                        bgcolor: 'primary.lighter',
                                        px: 2,
                                        minHeight: 56,
                                        '&.Mui-expanded': { minHeight: 56 }
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          width: '100%',
                                          pr: 2,
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center'
                                        }}
                                      >
                                        <Box>
                                          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                                            <Typography variant="subtitle1" fontWeight={800}>
                                              NOS:
                                            </Typography>
                                            <Chip label={nos.nosCode} size="small" color="primary" sx={{ fontWeight: 700 }} />
                                            <Typography variant="subtitle1" fontWeight={800} color="primary.main">
                                              {nos.nosName}
                                            </Typography>
                                          </Stack>
                                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                            Total Marks: {nosMax}
                                          </Typography>
                                        </Box>
                                        <Box sx={{ width: 180, textAlign: 'right', flexShrink: 0 }}>
                                          <Typography variant="subtitle2" fontWeight={800} color="primary.main">
                                            {nosTotal} / {nosMax}
                                          </Typography>
                                          <LinearProgress
                                            variant="determinate"
                                            value={Math.min(nosPercent, 100)}
                                            color={nosPercent >= 80 ? 'success' : nosPercent >= 50 ? 'warning' : 'primary'}
                                            sx={{ height: 6, borderRadius: 1, mt: 0.5 }}
                                          />
                                        </Box>
                                      </Box>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ p: 0, bgcolor: 'background.paper' }}>
                                      <Divider />
                                      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        {(nos.pcs || []).map((pc: any, pcIdx: number) => {
                                          const pcId = String(pc?.pcId?._id || pc?.pcId);
                                          const pcData = marksByPc[pcId] || {};
                                          const questions = pcData.questions || [];
                                          const pcTotal = questions.reduce((sum: number, q: any) => sum + (Number(q.marks) || 0), 0);
                                          const pcMax = Number(pc.totalMarks ?? pcData.maxMarks ?? 0);
                                          const pcPercent = pcMax > 0 ? Math.round((pcTotal / pcMax) * 100) : 0;
                                          const pcTypeLabel =
                                            pc?.type?.title ||
                                            (Array.isArray(pc?.type)
                                              ? pc.type
                                                  .map((t: any) => t?.title)
                                                  .filter(Boolean)
                                                  .join(' / ')
                                              : pc?.type) ||
                                            '-';

                                          return (
                                            <Accordion
                                              key={pcId || pcIdx}
                                              defaultExpanded={pcIdx === 0}
                                              disableGutters
                                              elevation={0}
                                              sx={{
                                                '&:before': { display: 'none' },
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                borderRadius: '8px !important',
                                                overflow: 'hidden',
                                                '& .MuiAccordionSummary-root': { bgcolor: 'grey.50' },
                                                '& .MuiAccordionDetails-root': { p: 1.5 }
                                              }}
                                            >
                                              <AccordionSummary
                                                expandIcon={<ArrowDown2 size={16} />}
                                                sx={{ minHeight: 48, '&.Mui-expanded': { minHeight: 48 } }}
                                              >
                                                <Box
                                                  sx={{
                                                    width: '100%',
                                                    pr: 2,
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center'
                                                  }}
                                                >
                                                  <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                                                    <Typography variant="subtitle2" fontWeight={800}>
                                                      {pc.pcCode} — {pc.pcName}
                                                    </Typography>
                                                    <Chip
                                                      size="small"
                                                      variant="outlined"
                                                      sx={{ height: 20, fontWeight: 500, fontSize: '0.78rem' }}
                                                      label={`${pcTypeLabel} · ${pc.isCritical ? 'Critical' : 'Non-critical'}`}
                                                    />
                                                  </Stack>
                                                  <Box sx={{ width: 160, textAlign: 'right', flexShrink: 0 }}>
                                                    <Typography variant="caption" fontWeight={800}>
                                                      {pcTotal} / {pcMax}
                                                    </Typography>
                                                    <LinearProgress
                                                      variant="determinate"
                                                      value={Math.min(pcPercent, 100)}
                                                      color={pcPercent >= 80 ? 'success' : pcPercent >= 50 ? 'warning' : 'primary'}
                                                      sx={{ height: 5, borderRadius: 1, mt: 0.25 }}
                                                    />
                                                  </Box>
                                                </Box>
                                              </AccordionSummary>
                                              <AccordionDetails>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                  {questions.length > 0 ? (
                                                    questions.map((q: any, qIdx: number) => (
                                                      <Box
                                                        key={String(q.qId || qIdx)}
                                                        sx={{
                                                          display: 'flex',
                                                          alignItems: 'center',
                                                          gap: 2,
                                                          p: 1.25,
                                                          borderRadius: 1,
                                                          bgcolor: 'background.paper',
                                                          border: '1px solid',
                                                          borderColor: 'divider'
                                                        }}
                                                      >
                                                        <Chip
                                                          label={`Q${qIdx + 1}`}
                                                          size="small"
                                                          color="secondary"
                                                          sx={{ fontWeight: 800, minWidth: 40 }}
                                                        />
                                                        <Box sx={{ flex: 1 }}>
                                                          <Typography variant="body2" fontWeight={700}>
                                                            {q.questionText || `Question ${qIdx + 1}`}
                                                          </Typography>
                                                          <Typography variant="caption" color="text.secondary">
                                                            Max: {q.maxMarks || '-'} | Obtained: {Number(q.marks) || 0}
                                                          </Typography>
                                                        </Box>
                                                        <Typography variant="body2" fontWeight={800} color="primary.main">
                                                          {Number(q.marks) || 0}
                                                        </Typography>
                                                      </Box>
                                                    ))
                                                  ) : (
                                                    <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                                                      No questions available for this PC.
                                                    </Typography>
                                                  )}
                                                </Box>
                                              </AccordionDetails>
                                            </Accordion>
                                          );
                                        })}
                                      </Box>
                                    </AccordionDetails>
                                  </Accordion>
                                );
                              })}
                              <Box sx={{ p: 2, bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider' }}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                  <Box>
                                    <Typography variant="body2" color="text.secondary">
                                      Grand Total
                                    </Typography>
                                    <Typography variant="h6" fontWeight={800}>
                                      {grandTotal} / {grandMax}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </Box>
                            </Box>
                          );
                        })()}

                      {/* Eval failed / no data after load */}
                      {evalState && !evalState.loading && !evalState.data && (
                        <Typography variant="body2" color="text.secondary">
                          No question-wise data available.
                        </Typography>
                      )}
                    </AccordionDetails>
                  </Accordion>
                );
              })
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ResultDetails;
